/**
 * **Laporan harian** — penjualan per outlet × tanggal usaha, setoran per jenis tender, tutup shift.
 *
 * ### ⛔ Layar TIDAK menghitung apa pun
 * Semua angka datang jadi dari server. Laporan yang dirakit di dua tempat akan menyimpang — dan
 * yang menyimpang di sini adalah angka yang dipakai orang menutup laci dan membayar pajak.
 *
 * ### ⭐⭐ Tiga hal yang sengaja dibuat MENYOLOK, bukan halus
 * 1. **Σ tender ≠ Σ penjualan** ⇒ lencana merah. Itu invarian §7 yang seharusnya selalu benar;
 *    kalau ia meleset, datanya rusak dan pembaca harus tahu SEBELUM memakai angkanya.
 * 2. **Selisih kas** ⇒ merah + alasannya di sebelahnya. Kontrol "selisih wajib beralasan" sudah
 *    memaksa di titik tutup, tapi kalau alasannya tak pernah terbaca, ia merosot jadi formalitas
 *    yang diketik asal. Bukti Arena: alasan terisi **17 dari 8.125 hari**, selisih −Rp89 jt/outlet.
 * 3. **Shift yang masih TERBUKA** ⇒ ditandai. Sesi yang menggantung berhari-hari mencampur
 *    beberapa hari jadi satu, dan satu-satunya cara ia ketahuan adalah dengan terlihat di sini.
 *
 * ### ⚠️ "Hari ini" di pemilih tanggal = tanggal KALENDER peramban
 * Laporannya sendiri mengelompokkan menurut **tanggal usaha** (yang berpindah di cutover
 * 04:00/05:00). Jadi memilih "hari ini" jam 02:00 dini hari akan menampilkan hari usaha yang
 * BELUM ditutup. Dinyatakan di layar, bukan dibiarkan jadi kejutan.
 */
import { Alert, Button, Card, DatePicker, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type {
  DailySales, DailySalesRow, DailyTenderRow, ItemSalesRow, KitchenTimingRow, Outlet, PriceDriftRow,
  ShiftRow, TaxRecap,
  TaxRecapRow,
} from '../api/client';

/** Rupiah untuk TAMPILAN. ⛔ Tak pernah dipakai berhitung — angkanya sudah final dari server. */
function rp(v: string | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const [utuh = '0'] = v.split('.');
  const neg = utuh.startsWith('-');
  const d = (neg ? utuh.slice(1) : utuh).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${neg ? '-' : ''}${d}`;
}

const KOSONG: DailySales = { rows: [], tenders: [] };
const KOSONG_PAJAK: TaxRecap = {
  rows: [],
  sales: { bills: 0, grossPaid: '0.0000', discountTotal: '0.0000', grandTotal: '0.0000' },
  reconciliation: { recapTotal: '0.0000', orderRollupTotal: '0.0000', balanced: true },
};

/**
 * ⭐⭐ **Unduh CSV** — rekap pajak disalin ke pelaporan, dan menyalinnya dari layar dengan tangan
 * adalah tempat kesalahan ketik masuk ke SPT.
 *
 * ⛔ Dibentuk dari baris yang SAMA dengan yang tampil, bukan dari permintaan kedua ke server:
 * berkas yang isinya beda dari layar adalah cara paling halus untuk kehilangan kepercayaan.
 * ⚠️ Pemisah TITIK KOMA — Excel Indonesia memakai koma sebagai desimal, dan CSV berkoma akan
 * memecah satu angka jadi dua kolom.
 */
function unduhCsv(nama: string, baris: readonly (readonly string[])[]): void {
  const isi = baris
    .map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  /* BOM supaya Excel membaca UTF-8 — tanpa itu nama outlet beraksen jadi kacau. */
  const url = URL.createObjectURL(new Blob(['\ufeff' + isi], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nama;
  a.click();
  URL.revokeObjectURL(url);
}

export function Reports() {
  const { t } = useTranslation();
  const [outlets, setOutlets] = useState<readonly Outlet[]>([]);
  const [outletCode, setOutletCode] = useState<string | undefined>(undefined);
  /* ⭐ Bawaan 7 hari terakhir: cukup untuk "kemarin bagaimana" tanpa memindai apa pun. */
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [sales, setSales] = useState<DailySales>(KOSONG);
  const [shifts, setShifts] = useState<readonly ShiftRow[]>([]);
  const [pajak, setPajak] = useState<TaxRecap>(KOSONG_PAJAK);
  /**
   * ⭐⭐⭐ Penjualan per ITEM — pertanyaan pertama tiap pemilik F&B, dan sampai 2026-08-23
   * permukaan laporan tak bisa menjawabnya sama sekali.
   */
  const [item, setItem] = useState<readonly ItemSalesRow[]>([]);
  const [drift, setDrift] = useState<readonly PriceDriftRow[]>([]);
  /** ⭐ Waktu masak per stasiun — SLA yang disetel sejak 0043 tapi tak pernah diukur. */
  const [dapur, setDapur] = useState<readonly KitchenTimingRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void api.outlets().then(setOutlets).catch(() => undefined); }, []);

  const muat = useCallback(async () => {
    setBusy(true);
    setError(null);
    const from = range[0].format('YYYY-MM-DD');
    const to = range[1].format('YYYY-MM-DD');
    try {
      /*
       * ⭐ Yang POKOK — penjualan, sesi kas, rekap pajak. Kegagalan di sini memang harus
       * menjatuhkan layarnya: angka setengah di laporan lebih berbahaya daripada tak ada angka.
       */
      const [s, sh, px] = await Promise.all([
        api.dailySales(from, to, outletCode),
        api.shiftReport(from, to, outletCode),
        api.taxRecap(from, to, outletCode),
      ]);
      setSales(s);
      setShifts(sh.rows);
      setPajak(px);
      /*
       * ⛔⛔ **Kartu TAMBAHAN ditarik terpisah, masing-masing dengan catch-nya sendiri** — dan ini
       * bekas luka yang sudah menggigit dua kali (penjualan per item, lalu waktu dapur).
       *
       * Saat keduanya masih ikut `Promise.all` di atas, satu kegagalan di kartu paling pinggir
       * mengosongkan SELURUH layar: penjualan, rekap pajak, sesi kas, semuanya lenyap, dan yang
       * tampil cuma pesan galat yang menyebut endpoint yang tak sedang dilihat siapa pun.
       *
       * ⚠️ Angka penjualan & pajak adalah alasan orang membuka layar ini; waktu masak adalah
       * bonus. Yang bonus tak boleh bisa menjatuhkan yang pokok.
       */
      await Promise.all([
        api.itemSales(from, to, outletCode)
          .then((it) => { setItem(it.rows); })
          .catch(() => { setItem([]); }),
        api.kitchenTiming(from, to, outletCode)
          .then((kt) => { setDapur(kt.rows); })
          .catch(() => { setDapur([]); }),
        api.priceDrift(from, to, outletCode)
          .then((pd) => { setDrift(pd.rows); })
          .catch(() => { setDrift([]); }),
      ]);
    } catch (e: unknown) {
      /* ⛔ Pesan server APA ADANYA — `REPORT-400-RANGE` sudah menyebut sebab & batasnya. */
      setError(e instanceof Error ? e.message : String(e));
      setSales(KOSONG);
      setShifts([]);
      setPajak(KOSONG_PAJAK);
      setItem([]);
      setDapur([]);
      setDrift([]);
    } finally {
      setBusy(false);
    }
  }, [range, outletCode]);

  useEffect(() => { void muat(); }, [muat]);

  const total = sales.rows.reduce(
    (a, r) => ({
      bills: a.bills + r.bills,
      guests: a.guests + r.guests,
      grand: a.grand + Number(r.grandTotal),
    }),
    { bills: 0, guests: 0, grand: 0 },
  );
  const timpang = sales.rows.filter((r: DailySalesRow) => !r.balanced);

  const kolomJual: ColumnsType<DailySalesRow> = [
    { title: t('report.date'), dataIndex: 'businessDate', width: 116 },
    { title: t('report.outlet'), render: (_, r) => `${r.outletCode} · ${r.outletName}` },
    { title: t('report.bills'), dataIndex: 'bills', align: 'right', width: 80 },
    { title: t('report.guests'), dataIndex: 'guests', align: 'right', width: 80 },
    { title: t('report.gross'), align: 'right', render: (_, r) => rp(r.subtotalGross) },
    { title: t('report.discount'), align: 'right', render: (_, r) => rp(r.discountTotal) },
    { title: t('report.serviceCharge'), align: 'right', render: (_, r) => rp(r.serviceChargeTotal) },
    { title: t('report.tax'), align: 'right', render: (_, r) => rp(r.taxTotal) },
    {
      title: t('report.grandTotal'),
      align: 'right',
      render: (_, r) => <strong>{rp(r.grandTotal)}</strong>,
    },
    {
      title: t('report.tenderTotal'),
      align: 'right',
      render: (_, r) => (
        /*
          ⛔⛔ Ketidakcocokan DINYATAKAN, tak diserahkan ke pembaca untuk dijumlahkan sendiri.
          Σ tender = Σ grand total adalah invarian §7; kalau meleset, datanya yang rusak.
        */
        r.balanced
          ? rp(r.tenderTotal)
          : (
            <Tag color="error" data-testid={`report-timpang-${r.businessDate}-${r.outletCode}`}>
              {rp(r.tenderTotal)}
            </Tag>
          )
      ),
    },
  ];

  const kolomTender: ColumnsType<DailyTenderRow> = [
    { title: t('report.date'), dataIndex: 'businessDate', width: 116 },
    { title: t('report.outlet'), dataIndex: 'outletCode', width: 110 },
    {
      /* ⭐ Makna EKONOMI, bukan nama metode: itu yang membedakan uang di laci dari yang belum. */
      title: t('report.tenderType'),
      /* ⚠️ Label dari kamus `payments.tender.*` yang SUDAH ADA — bukan kamus kedua. Nilai enum yang
         belum berlabel tampil apa adanya, bukan kosong: kode mentah masih bisa dicari orang. */
      render: (_, r) => <Tag>{t(`payments.tender.${r.tenderType}`, { defaultValue: r.tenderType })}</Tag>,
    },
    { title: t('report.count'), dataIndex: 'count', align: 'right', width: 80 },
    {
      title: t('report.amount'),
      align: 'right',
      render: (_, r) => <strong>{rp(r.amount)}</strong>,
    },
    { title: t('report.received'), align: 'right', render: (_, r) => rp(r.tenderedAmount) },
    { title: t('report.change'), align: 'right', render: (_, r) => rp(r.changeGiven) },
  ];



  /**
   * ⭐⭐⭐ **Penyimpangan harga** — akibat yang HARUS terlihat dari keputusan menerima angka outlet.
   *
   * Sejak 2026-08-25 portal menyimpan harga yang benar-benar ditagihkan di outlet, bukan harga
   * masternya (Imron: *"portal harus menerima apapun yang dikirim outlet"*). Itu benar — uang yang
   * sudah berpindah tangan tak boleh ditahan data HQ. ⛔ Tapi tanpa tabel ini, till yang katalognya
   * tertinggal bisa jualan berbulan-bulan tanpa satu baris pun yang menunjukkannya.
   *
   * ⚠️ Yang ditonjolkan **selisihnya**, bukan harganya: dua angka berdampingan menuntut pembaca
   * mengurangi sendiri, dan yang harus dikurangi sendiri tak pernah dibaca sampai selesai.
   */
  const kolomDrift: ColumnsType<PriceDriftRow> = [
    { title: t('report.outlet'), dataIndex: 'outletCode', width: 90 },
    { title: t('report.drift.order'), dataIndex: 'orderNo', width: 130 },
    /* ⚠️ Perangkatnya ikut — inilah yang menjawab "till MANA yang basi", bukan sekadar "ada". */
    { title: t('report.drift.device'), dataIndex: 'deviceCode', width: 110,
      render: (v: string | null) => v ?? '—' },
    { title: t('report.drift.product'), dataIndex: 'productCode', width: 110 },
    { title: t('report.drift.qty'), dataIndex: 'qty', width: 70, align: 'right',
      render: (v: string) => v.replace(/\.0+$/, '') },
    { title: t('report.drift.charged'), dataIndex: 'charged', width: 110, align: 'right',
      render: (v: string) => rp(v) },
    { title: t('report.drift.master'), dataIndex: 'master', width: 110, align: 'right',
      render: (v: string) => rp(v) },
    {
      title: t('report.drift.delta'), dataIndex: 'delta', width: 120, align: 'right',
      render: (v: string) => (
        <Tag color={v.startsWith('-') ? 'red' : 'orange'} data-testid="drift-delta">{rp(v)}</Tag>
      ),
    },
    /* ⭐ Versi katalog: dua till yang menyimpang dengan versi yang SAMA berarti masternya yang
       berubah; versi berbeda berarti till itu sendiri yang belum SYNC. */
    { title: t('report.drift.catalog'), dataIndex: 'catalogVersion', width: 150,
      render: (v: string | null) => v ?? '—' },
  ];

  const kolomItem: ColumnsType<ItemSalesRow> = [
    { title: t('report.outlet'), width: 110, dataIndex: 'outletCode' },
    {
      title: t('report.item'),
      render: (_, r) => (
        <>
          <Typography.Text type="secondary">{r.productCode}</Typography.Text> {r.productName}
        </>
      ),
    },
    { title: t('report.category'), width: 150, render: (_, r) => r.categoryName ?? '—' },
    {
      title: t('report.qtySold'),
      align: 'right', width: 90,
      render: (_, r) => Number(r.qtySold).toFixed(0),
    },
    { title: t('report.netSold'), align: 'right', render: (_, r) => rp(r.netSold) },
    {
      /* ⭐ Nol ditampilkan REDUP, bukan kosong: kolom kosong terbaca sebagai "belum dihitung". */
      title: t('report.refunded'),
      align: 'right',
      render: (_, r) => (
        Number(r.netRefunded) === 0
          ? <Typography.Text type="secondary">{rp('0')}</Typography.Text>
          : <Typography.Text type="danger">{rp(r.netRefunded)}</Typography.Text>
      ),
    },
    {
      title: t('report.netAfterRefund'),
      align: 'right',
      render: (_, r) => <strong>{rp(r.netAfterRefund)}</strong>,
    },
    {
      /* ⛔ Porsi dihitung SERVER — dua layar yang membaginya sendiri akan berbeda pembulatannya,
         dan persen yang berbeda antar-layar membuat keduanya berhenti dipercaya. */
      title: t('report.share'),
      align: 'right', width: 90,
      render: (_, r) => `${(Number(r.share) * 100).toFixed(1)}%`,
    },
  ];

  /** Detik → "3m 20s". ⚠️ Tampilan saja; angkanya sudah final dari server. */
  const durasi = (s: number): string =>
    s < 60 ? `${String(s)}s` : `${String(Math.floor(s / 60))}m ${String(s % 60)}s`;

  const kolomDapur: ColumnsType<KitchenTimingRow> = [
    { title: t('report.outlet'), width: 110, dataIndex: 'outletCode' },
    { title: t('report.station'), render: (_, r) => `${r.stationCode} · ${r.stationName}` },
    { title: t('report.tickets'), align: 'right', width: 90, dataIndex: 'tickets' },
    { title: t('report.avg'), align: 'right', render: (_, r) => durasi(r.avgSeconds) },
    { title: 'p50', align: 'right', render: (_, r) => durasi(r.p50Seconds) },
    {
      /* ⭐ p90 ditebalkan: inilah yang menjawab "seberapa sering tamu menunggu lama". */
      title: 'p90',
      align: 'right',
      render: (_, r) => <strong>{durasi(r.p90Seconds)}</strong>,
    },
    {
      title: t('report.late'),
      align: 'right', width: 100,
      render: (_, r) => (
        r.lateCount === 0
          ? <Typography.Text type="secondary">0</Typography.Text>
          : <Typography.Text type="danger">{r.lateCount}</Typography.Text>
      ),
    },
    {
      /* ⛔ Yang belum di-bump DIKATAKAN — kalau disembunyikan, dapur yang lupa terlihat sempurna. */
      title: t('report.unfinished'),
      align: 'right', width: 110,
      render: (_, r) => (
        r.unfinished === 0
          ? <Typography.Text type="secondary">0</Typography.Text>
          : <Tag color="orange" data-testid={`dapur-gantung-${r.stationCode}`}>{r.unfinished}</Tag>
      ),
    },
  ];

  const kolomPajak: ColumnsType<TaxRecapRow> = [
    { title: t('report.outlet'), render: (_, r) => `${r.outletCode} · ${r.outletName}` },
    /* ⭐ KODE lebih dulu — itu kuncinya; nama cuma untuk dibaca. */
    { title: t('report.taxCode'), dataIndex: 'taxCode', width: 110 },
    { title: t('report.taxName'), dataIndex: 'name' },
    {
      title: t('report.taxKind'),
      width: 150,
      render: (_, r) => (
        <span>
          <Tag>{r.kind}</Tag>
          {/* ⚠️ Pajak INKLUSIF ditandai: DPP-nya dihitung mundur dari harga, dan yang melapor
              wajib tahu bedanya sebelum menyalin. */}
          {r.isInclusive && <Tag color="processing">{t('report.inclusive')}</Tag>}
        </span>
      ),
    },
    {
      title: t('report.rate'),
      align: 'right',
      width: 90,
      render: (_, r) => `${(Number(r.rate) * 100).toFixed(2)}%`,
    },
    { title: t('report.dpp'), align: 'right', render: (_, r) => rp(r.taxableBase) },
    {
      title: t('report.taxAmount'),
      align: 'right',
      render: (_, r) => <strong>{rp(r.taxAmount)}</strong>,
    },
  ];

  const kolomShift: ColumnsType<ShiftRow> = [
    { title: t('report.date'), dataIndex: 'businessDate', width: 116 },
    { title: t('report.outlet'), dataIndex: 'outletCode', width: 110 },
    { title: t('report.shiftNo'), dataIndex: 'shiftNo' },
    { title: t('report.openedBy'), dataIndex: 'openedByName' },
    {
      title: t('report.status'),
      width: 110,
      render: (_, r) => (
        /* ⭐⭐ Sesi yang menggantung berhari-hari hanya ketahuan kalau ia TERLIHAT di sini. */
        r.status === 'open'
          ? <Tag color="warning" data-testid="report-shift-open">{t('report.stillOpen')}</Tag>
          : <Tag>{r.status}</Tag>
      ),
    },
    { title: t('report.expectedCash'), align: 'right', render: (_, r) => rp(r.expectedCash) },
    { title: t('report.countedCash'), align: 'right', render: (_, r) => rp(r.countedCash) },
    {
      title: t('report.variance'),
      align: 'right',
      render: (_, r) => {
        const v = Number(r.variance ?? '0');
        if (r.variance === null) return '—';
        if (v === 0) return rp(r.variance);
        return (
          <Tag color="error" data-testid="report-variance">{rp(r.variance)}</Tag>
        );
      },
    },
    {
      /* ⛔ Alasan DI SEBELAH angkanya. Kontrol yang alasannya tak pernah dibaca akan diketik asal. */
      title: t('report.reason'),
      render: (_, r) => (
        r.varianceReasonCode === null ? '—' : (
          <span>
            <Tag>{r.varianceReasonCode}</Tag>
            {r.varianceReasonNote ?? ''}
          </span>
        )
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="reports">
      <Card size="small">
        <Space wrap>
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            data-testid="report-range"
            onChange={(v) => { if (v?.[0] && v[1]) setRange([v[0], v[1]]); }}
          />
          <Select
            allowClear
            style={{ minWidth: 220 }}
            placeholder={t('report.allOutlets')}
            data-testid="report-outlet"
            value={outletCode}
            onChange={setOutletCode}
            options={outlets.map((o) => ({ value: o.code, label: `${o.code} · ${o.name}` }))}
          />
          <Button loading={busy} onClick={() => void muat()} data-testid="report-refresh">
            {t('report.refresh')}
          </Button>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Typography.Text type="secondary">{t('report.businessDateHint')}</Typography.Text>
        </div>
      </Card>

      {error !== null && <Alert type="error" showIcon data-testid="report-error" title={error} />}

      {timpang.length > 0 && (
        <Alert
          type="error"
          showIcon
          data-testid="report-imbalance"
          title={t('report.imbalance', { n: timpang.length })}
        />
      )}

      <Card
        size="small"
        title={t('report.salesTitle')}
        extra={
          <Typography.Text data-testid="report-total">
            {t('report.summary', {
              bills: total.bills, guests: total.guests, amount: rp(`${String(total.grand)}.0000`),
            })}
          </Typography.Text>
        }
      >
        <Table
          size="small"
          loading={busy}
          rowKey={(r) => `${r.businessDate}/${r.outletCode}`}
          columns={kolomJual}
          dataSource={sales.rows as DailySalesRow[]}
          pagination={false}
          scroll={{ x: true }}
          data-testid="report-sales"
        />
      </Card>

      <Card size="small" title={t('report.tenderTitle')}>
        <Table
          size="small"
          loading={busy}
          rowKey={(r) => `${r.businessDate}/${r.outletCode}/${r.tenderType}`}
          columns={kolomTender}
          dataSource={sales.tenders as DailyTenderRow[]}
          pagination={false}
          scroll={{ x: true }}
          data-testid="report-tenders"
        />
      </Card>

      {/*
        ⭐⭐⭐ **Penjualan per ITEM** — *"menu mana yang laku?"*.

        ⚠️ Kotor · dikembalikan · bersih ditampilkan BERDAMPINGAN. Kalau cuma yang bersih yang
        tampil, item yang laku keras TAPI banyak dikembalikan (dapur bermasalah, porsi meleset,
        resep berubah) terlihat persis sama dengan item yang biasa-biasa saja — dan itu justru
        sinyal paling berharga di tabel ini.
      */}
      <Card size="small" title={t('report.itemTitle')}>
        <Table
          size="small"
          loading={busy}
          rowKey={(r) => `${r.outletCode}/${r.productCode}`}
          columns={kolomItem}
          dataSource={item as ItemSalesRow[]}
          pagination={{ pageSize: 25, size: 'small' }}
          scroll={{ x: true }}
          data-testid="report-items"
        />
      </Card>

      {/*
        ⭐⭐⭐ **Penyimpangan harga** — sengaja diletakkan SESUDAH penjualan per item: ia menjelaskan
        angka di atasnya, dan yang menjelaskan tak boleh berdiri sebelum yang dijelaskan.

        ⚠️ Kosong = kabar BAIK, dan itu dikatakan. Tabel kosong tanpa keterangan terbaca sebagai
        "laporannya rusak", dan yang dikira rusak berhenti dibuka.
      */}
      <Card size="small" title={t('report.drift.title')}>
        {drift.length === 0 ? (
          <Typography.Text type="secondary" data-testid="drift-none">
            {t('report.drift.none')}
          </Typography.Text>
        ) : (
          <Table
            size="small"
            loading={busy}
            rowKey={(r) => `${r.orderNo}/${r.productCode}/${r.at}`}
            columns={kolomDrift}
            dataSource={drift as PriceDriftRow[]}
            pagination={{ pageSize: 25, size: 'small' }}
            scroll={{ x: true }}
            data-testid="report-drift"
          />
        )}
      </Card>

      {/*
        ⭐⭐⭐ **Waktu masak** — ambang SLA (0043) akhirnya bisa diperiksa.

        ⛔ Sampai 2026-08-23 manajer bisa menyetel target dan tak akan pernah tahu apakah ia
        ditepati. Setelan yang menuntut kepatuhan tanpa pernah memeriksa adalah setelan yang
        berhenti dipercaya (Guardrail #6).

        ⚠️ p50 & p90 dibawa berdampingan dengan rata-rata, karena ketiganya menjawab pertanyaan
        yang berbeda — dan rata-rata sendirian menyembunyikan justru yang dicari.
      */}
      <Card size="small" title={t('report.kitchenTitle')}>
        <Table
          size="small"
          loading={busy}
          rowKey={(r) => `${r.outletCode}/${r.stationCode}`}
          columns={kolomDapur}
          dataSource={dapur as KitchenTimingRow[]}
          pagination={false}
          scroll={{ x: true }}
          data-testid="report-kitchen"
        />
      </Card>

      {/*
        ⭐⭐ Rekap pajak DI ATAS tutup shift: ia yang disalin ke pelaporan bulanan, dan yang
        disalin orang harus ditemukan tanpa menggulir.
      */}
      <Card
        size="small"
        title={t('report.taxTitle')}
        extra={
          <Button
            size="small"
            data-testid="report-tax-csv"
            disabled={pajak.rows.length === 0}
            onClick={() => {
              unduhCsv(
                `rekap-pajak_${range[0].format('YYYY-MM-DD')}_${range[1].format('YYYY-MM-DD')}.csv`,
                [
                  ['outlet_code', 'outlet_name', 'tax_code', 'name', 'kind', 'rate',
                    'is_inclusive', 'taxable_base', 'tax_amount', 'lines'],
                  ...pajak.rows.map((r) => [
                    r.outletCode, r.outletName, r.taxCode, r.name, r.kind, r.rate,
                    String(r.isInclusive), r.taxableBase, r.taxAmount, String(r.lines),
                  ]),
                ],
              );
            }}
          >
            {t('report.downloadCsv')}
          </Button>
        }
      >
        {/*
          ⛔⛔ Dua jalur menuju angka yang sama: Σ baris pajak vs rollup pesanan. Kalau berbeda,
          yang melapor pajak harus tahu SEBELUM menyalin — bukan sesudah menyetor.
        */}
        {!pajak.reconciliation.balanced && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            data-testid="report-tax-mismatch"
            title={t('report.taxMismatch', {
              recap: rp(pajak.reconciliation.recapTotal),
              rollup: rp(pajak.reconciliation.orderRollupTotal),
            })}
          />
        )}
        <Typography.Paragraph type="secondary" data-testid="report-tax-base">
          {t('report.taxBaseHint', {
            bills: pajak.sales.bills,
            gross: rp(pajak.sales.grossPaid),
            grand: rp(pajak.sales.grandTotal),
          })}
        </Typography.Paragraph>
        <Table
          size="small"
          loading={busy}
          rowKey={(r) => `${r.outletCode}/${r.taxCode}`}
          columns={kolomPajak}
          dataSource={pajak.rows as TaxRecapRow[]}
          pagination={false}
          scroll={{ x: true }}
          data-testid="report-tax"
        />
      </Card>

      <Card size="small" title={t('report.shiftTitle')}>
        <Table
          size="small"
          loading={busy}
          rowKey={(r) => `${r.businessDate}/${r.outletCode}/${r.shiftNo}`}
          columns={kolomShift}
          dataSource={shifts as ShiftRow[]}
          pagination={false}
          scroll={{ x: true }}
          data-testid="report-shifts"
        />
      </Card>
    </Space>
  );
}
